import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { TEXT_TO_SPEECH_VOICE_NAME } from 'shared/constants/limits'
import { logger } from 'shared/lib'
import { type IResponseVoice } from 'entities/TextToSpeech'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ModalContent } from 'widgets/NewCustomVoiceModal/ui/ModalContent'
import { RenameModalStore } from 'widgets/RenameModal'
import { RecordAudioStore } from 'widgets/RecordAudio'
import { topics } from 'widgets/NewCustomVoiceModal/constants/constants'

type INewCustomVoiceStoreProps = {
  onSuccessCreateVoice: (voice: IResponseVoice) => void
}

export type ITopic = 'generic' | 'sales' | 'marketing' | 'customer-support'

export class NewCustomVoiceStore {
  constructor(
    { onSuccessCreateVoice }: INewCustomVoiceStoreProps,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
    this.onSuccessCreateVoice = onSuccessCreateVoice
    this.open()
  }

  onSuccessCreateVoice: (voice: IResponseVoice) => void
  modalId = 'newCustomVoice'
  selectedTopic: ITopic = 'marketing'

  recordAudioStore: RecordAudioStore = new RecordAudioStore({
    AUDIO_RECORD_LIMIT: 180,
    withDelay: true,
  })

  get topicText() {
    return topics[this.selectedTopic].text
  }

  private onClose = () => {
    if (this.recordAudioStore.recordStore.micRecorder?.getRecordingDuration()) {
      if (
        this.recordAudioStore.recordStore.startRecord &&
        !this.recordAudioStore.recordStore.pauseRecord
      ) {
        this.recordAudioStore.recordStore.onPause()
      }

      this.discardChanges()
    } else {
      modalStore.removeModal(this.modalId)
    }
  }

  private discardChanges = (discardAction?: () => void) => {
    const id = nanoid()
    modalStore.addModal({
      id,
      type: ModalTypeList.WARNING,
      title: 'Leaving will discard your content',
      primaryAction: {
        text: 'Keep content',
        onAction: () => {
          modalStore.removeModal(id)
        },
      },
      secondaryAction: {
        text: 'Discard',
        onAction: () => {
          discardAction && discardAction()
          modalStore.removeModal(id)
          modalStore.removeModal(this.modalId)
        },
      },
    })
  }

  private open = () => {
    modalStore.addModal({
      id: this.modalId,
      title: 'Create custom voice',
      ModalContent: ModalContent,
      pureContent: true,
      ModalContentProps: { store: this },
      onClose: this.onClose,
    })

    const handleFinish = (file: File) => {
      modalStore.setHideModalId(this.modalId)
      const nameModalId = nanoid()
      const closeNameModal = () => {
        modalStore.removeHideModalId(this.modalId)
        modalStore.removeModal(nameModalId)
      }
      new RenameModalStore({
        id: nameModalId,
        title: 'Voice name',
        element: 'custom voice',
        maxNameLength: TEXT_TO_SPEECH_VOICE_NAME,
        secondaryAction: {
          text: 'Back',
          onAction: closeNameModal,
        },
        onClose: () => {
          this.discardChanges(closeNameModal)
        },

        onSave: async (name: string) => {
          try {
            const voice = await this._entities.textToSpeechStore.createCustomVoice({
              sample: file,
              name,
            })
            if (voice) {
              this.onSuccessCreateVoice(voice)
              closeNameModal()
              modalStore.removeModal(this.modalId)
            }
          } catch (e) {
            logger.error(e)
          }
        },
      })
    }
    this.recordAudioStore.init({ handleFinish, handleCancel: this.onClose })
  }

  onSelectTopic = (topic: ITopic) => {
    this.selectedTopic = topic
  }
}
